<template>
      <div class="textarea-modern-wrapper">
    <!-- Main text input field -->
    <div
      ref="editorRef"
      class="textarea textarea-modern"
      :class="{ 
        focused: isFocused, 
        disabled: disabled, 
        autoresize: autoresize 
      }"
      contenteditable="true"
      @input="handleInput"
      @keydown="handleKeyDown" 
      @focus="handleFocus"
      @blur="handleBlur"
      @click="handleClick"
      @paste="handlePaste"
      :data-placeholder="placeholder"
      v-html="displayContent"
    ></div>

    <!-- Dropdown for user search -->
    <div 
      v-if="showUserSearch"
      class="user-search-dropdown"
      :style="dropdownStyle"
      ref="usersListRef"
    >
      <div v-if="isSearching" class="loading">
        Searching users...
      </div>
      <div 
        v-else-if="searchResults.length > 0"
        class="users-list"
      >
        <div
          v-for="(user, index) in searchResults" 
          :key="user.id"
          class="user-item"
          :class="{ selected: selectedUserIndex === index }"
          @mousedown.prevent.stop="selectUser(user)"
          @mouseenter="selectedUserIndex = index"
        >
          <img loading="lazy" 
            :src="user.avatar_little" 
            :alt="user.name"
            class="user-avatar"
          />
          <span class="user-name">{{ user.name }}</span>
        </div>
      </div>
      <div v-else class="no-results">
        No users found
      </div>
    </div>
  </div>
</template>


<script setup lang="ts">
import debounce from 'lodash/debounce'

// Types
interface User {
  id: number
  name: string
  avatar_little: string
  link: string
}

interface MentionData {
  userId: number
  userName: string
  start: number
  end: number
}

// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Enter text...'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  maxLength: {
    type: Number,
    default: null
  },
  autoresize: {
    type: Boolean,
    default: false
  },
  rows: {
    type: Number,
    default: 3
  }
})

// Emits
const emits = defineEmits(['update:modelValue', 'mention', 'focus', 'blur'])

// Reactive variables
const editorRef = ref<HTMLDivElement>()
const isFocused = ref(false)
const showUserSearch = ref(false)
const searchQuery = ref('')
const searchResults = ref<User[]>([])
const isSearching = ref(false)
const selectedUserIndex = ref(0)
const dropdownStyle = ref({})
const currentMentionStart = ref(0)
const minHeight = ref(0)
const lastPotentialMention = ref<{text: string, node: Text, start: number, end: number} | null>(null)
const usersListRef = ref<HTMLDivElement | null>(null)

// New approach with reactive content display
const plainText = ref('')
const displayContent = ref('')
const mentionsData = ref<MentionData[]>([])
const isInternalUpdate = ref(false)
const preventMentionSearch = ref(false)

// Composables
const { $api } = useNuxtApp()

// Utility functions for text/HTML conversion
const escapeHtml = (text: string): string => {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

const textToHtml = (text: string, mentions: MentionData[] = []): string => {
  if (!text) return '<br>'
  
  // Build segments of text and mentions in order
  const segments: Array<{type: 'text' | 'mention', content: string, start: number, end: number}> = []
  
  // Sort mentions by start position for processing
  const sortedMentions = [...mentions].sort((a, b) => a.start - b.start)
  
  let lastIndex = 0
  
  for (const mention of sortedMentions) {
    // Add text segment before mention
    if (mention.start > lastIndex) {
      segments.push({
        type: 'text',
        content: text.substring(lastIndex, mention.start),
        start: lastIndex,
        end: mention.start
      })
    }
    
    // Add mention segment
    segments.push({
      type: 'mention',
      content: `@${mention.userName}`,
      start: mention.start,
      end: mention.end
    })
    
    lastIndex = mention.end
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.substring(lastIndex),
      start: lastIndex,
      end: text.length
    })
  }
  
  // Build HTML
  let result = ''
  for (const segment of segments) {
    if (segment.type === 'text') {
      // Escape HTML and process line breaks for text segments only
      let textHtml = escapeHtml(segment.content)
      textHtml = textHtml.replace(/\n/g, '<br>')
      textHtml = textHtml.replace(/ {2,}/g, (match) => '&nbsp;'.repeat(match.length))
      result += textHtml
    } else {
      // Add mention HTML
      const mention = mentions.find(m => m.start === segment.start && m.end === segment.end)
      if (mention) {
        result += `<span class="mention" data-user-id="${mention.userId}" data-user-name="${escapeHtml(mention.userName)}" contenteditable="false">@${escapeHtml(mention.userName)}</span>`
      }
    }
  }
  
  // Handle trailing space
  if (text.endsWith(' ') && !result.endsWith('&nbsp;') && !result.endsWith('>')) {
    result = result.slice(0, -1) + '&nbsp;'
  }
  
  return result
}

const htmlToText = (html: string): {text: string, mentions: MentionData[]} => {
  if (!html) return {text: '', mentions: []}
  
  // Create temporary element to parse HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  
  const mentions: MentionData[] = []
  let text = ''
  let currentPos = 0
  
  // Walk through all nodes
  const walkNodes = (node: Node, isFirst = false) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const textContent = node.textContent || ''
      // Convert &nbsp; back to regular spaces
      const processedText = textContent.replace(/\u00A0/g, ' ')
      text += processedText
      currentPos += processedText.length
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element
      const tagName = element.tagName.toLowerCase()
      
      if (tagName === 'br') {
        text += '\n'
        currentPos += 1
      } else if (element.classList.contains('mention')) {
        const userName = element.getAttribute('data-user-name') || element.textContent?.substring(1) || ''
        const userId = parseInt(element.getAttribute('data-user-id') || '0')
        const mentionText = `@${userName}`
        
        mentions.push({
          userId,
          userName,
          start: currentPos,
          end: currentPos + mentionText.length
        })
        
        text += mentionText
        currentPos += mentionText.length
      } else if (['div', 'p'].includes(tagName)) {
        // Handle block elements - add newline before content (except for first element)
        if (!isFirst && (text.length > 0 && !text.endsWith('\n'))) {
          text += '\n'
          currentPos += 1
        }
        
        // Process children
        let isFirstChild = true
        for (const child of Array.from(node.childNodes)) {
          walkNodes(child, isFirstChild)
          isFirstChild = false
        }
        
        // Handle empty block elements
        if (node.childNodes.length === 0) {
          text += '\n'
          currentPos += 1
        }
      } else {
        // Process children for other elements
        for (const child of Array.from(node.childNodes)) {
          walkNodes(child, false)
        }
      }
    }
  }
  
  let isFirstChild = true
  for (const child of Array.from(tempDiv.childNodes)) {
    walkNodes(child, isFirstChild)
    isFirstChild = false
  }
  
  return {text, mentions}
}

const updateDisplayContent = () => {
  displayContent.value = textToHtml(plainText.value, mentionsData.value)
}

// Event handlers
const handleFocus = () => {
  isFocused.value = true
  emits('focus')
}



const handleClick = () => {
  // Update cursor position on click
  updateCursorPosition()
}

const handleInput = (event: Event) => {
  if (isInternalUpdate.value) return
  
  const target = event.target as HTMLDivElement
  const currentHtml = target.innerHTML
  
  // Parse current HTML to get text and mentions
  const {text, mentions} = htmlToText(currentHtml)
  
  // Update internal state
  plainText.value = text
  mentionsData.value = mentions
  
  // Emit the plain text
  emits('update:modelValue', text)
  
  // Check if user started mentioning
  checkForMention(text)
  
  // Auto-resize if enabled
  if (props.autoresize) {
    handleAutoResize()
  }
}

const handleBlur = () => {
  // Small delay to handle dropdown clicks
  setTimeout(() => {
    isFocused.value = false
    showUserSearch.value = false
    
    emits('blur')
  }, 200)
}

const handlePaste = (event: ClipboardEvent) => {
  // Prevent default paste behavior
  event.preventDefault()
  
  if (!editorRef.value) return
  
  // Get plain text from clipboard
  const clipboardData = event.clipboardData
  if (!clipboardData) return
  
  const textToInsert = clipboardData.getData('text/plain')
  if (!textToInsert) return
  
  // Insert plain text at cursor position
  insertPlainTextAtCursor(textToInsert)
}

// Insert plain text at current cursor position
const insertPlainTextAtCursor = (textToInsert: string) => {
  if (!editorRef.value) return
  
  const editor = editorRef.value
  const selection = window.getSelection()
  
  let newCursorPosition: number
  
  if (!selection || selection.rangeCount === 0) {
    // If no selection, append to the end
    newCursorPosition = plainText.value.length + textToInsert.length
    plainText.value += textToInsert
  } else {
    // Get current cursor position in plain text
    const cursorPos = getCursorPositionInPlainText()
    const selectedLength = getSelectedLengthInPlainText()
    
    // Calculate new cursor position (after inserted text)
    newCursorPosition = cursorPos + textToInsert.length
    
    // Insert text at cursor position
    const beforeCursor = plainText.value.substring(0, cursorPos)
    const afterCursor = plainText.value.substring(cursorPos + selectedLength)
    plainText.value = beforeCursor + textToInsert + afterCursor
    
    // Update mentions positions
    updateMentionsAfterTextInsertion(cursorPos, selectedLength, textToInsert.length)
  }
  
  // Update display and emit
  isInternalUpdate.value = true
  updateDisplayContent()
  emits('update:modelValue', plainText.value)
  
  // Set cursor at correct position after content update
  nextTick(() => {
    isInternalUpdate.value = false
    setCursorAtPosition(newCursorPosition)
    
    // Auto-resize if enabled
    if (props.autoresize) {
      handleAutoResize()
    }
  })
}

// Helper function to get cursor position in plain text
const getCursorPositionInPlainText = (): number => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !editorRef.value) return 0
  
  const range = selection.getRangeAt(0)
  const preCaretRange = range.cloneRange()
  preCaretRange.selectNodeContents(editorRef.value)
  preCaretRange.setEnd(range.startContainer, range.startOffset)
  
  // Convert HTML range to plain text position
  const htmlBeforeCursor = preCaretRange.cloneContents()
  const tempDiv = document.createElement('div')
  tempDiv.appendChild(htmlBeforeCursor)
  const {text} = htmlToText(tempDiv.innerHTML)
  
  return text.length
}

// Helper function to get selected text length in plain text
const getSelectedLengthInPlainText = (): number => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return 0
  
  const range = selection.getRangeAt(0)
  if (range.collapsed) return 0
  
  const selectedHtml = range.cloneContents()
  const tempDiv = document.createElement('div')
  tempDiv.appendChild(selectedHtml)
  const {text} = htmlToText(tempDiv.innerHTML)
  
  return text.length
}

// Helper function to update mentions positions after text insertion
const updateMentionsAfterTextInsertion = (insertPos: number, deletedLength: number, insertedLength: number) => {
  mentionsData.value = mentionsData.value.map(mention => {
    // If mention is completely before insertion point, no change
    if (mention.end <= insertPos) {
      return mention
    }
    
    // If mention is completely after insertion point, shift it
    if (mention.start >= insertPos + deletedLength) {
      return {
        ...mention,
        start: mention.start - deletedLength + insertedLength,
        end: mention.end - deletedLength + insertedLength
      }
    }
    
    // If mention overlaps with deletion area, remove it
    return null
  }).filter(Boolean) as MentionData[]
}

const handleKeyDown = (event: KeyboardEvent) => {
  // Handle user search dropdown navigation
  if (showUserSearch.value) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        selectedUserIndex.value = Math.min(selectedUserIndex.value + 1, searchResults.value.length - 1)
        nextTick(() => ensureSelectedItemVisible())
        break
      case 'ArrowUp':
        event.preventDefault()
        selectedUserIndex.value = Math.max(selectedUserIndex.value - 1, 0)
        nextTick(() => ensureSelectedItemVisible())
        break
      case 'Enter':
        event.preventDefault()
        if (searchResults.value[selectedUserIndex.value]) {
          selectUser(searchResults.value[selectedUserIndex.value])
        }
        break
      case 'Escape':
        event.preventDefault()
        showUserSearch.value = false
        break
    }
    return // Exit early when dropdown is active
  }

  // Only handle specific keys when not in search mode
  // Handle Backspace and Delete for mention removal
  if (event.key === 'Backspace' || event.key === 'Delete') {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    
    const range = selection.getRangeAt(0)
    const currentNode = range.startContainer
    
    if (event.key === 'Backspace') {
      handleBackspaceNearMention(event, range, currentNode)
    } else if (event.key === 'Delete') {
      handleDeleteNearMention(event, range, currentNode)
    }
  }
  
  // All other keys (arrows, ctrl+arrows, etc.) are handled by browser default behavior
}

// Handle Backspace near mention
const handleBackspaceNearMention = (event: KeyboardEvent, range: Range, currentNode: Node) => {
  const cursorPos = getCursorPositionInPlainText()
  
  // Check if we're trying to delete a mention
  const mentionToDelete = mentionsData.value.find(mention => 
    cursorPos === mention.end // Cursor right after mention
  )
  
  if (mentionToDelete) {
        event.preventDefault()
    
    // Prevent mention search immediately after deletion
    preventMentionSearch.value = true
    
    // Remove mention from text
    const beforeMention = plainText.value.substring(0, mentionToDelete.start)
    const afterMention = plainText.value.substring(mentionToDelete.end)
    plainText.value = beforeMention + afterMention
    
    // Remove mention from data
    mentionsData.value = mentionsData.value.filter(mention => mention !== mentionToDelete)
    
    // Update mentions positions
    updateMentionsAfterTextInsertion(mentionToDelete.start, mentionToDelete.end - mentionToDelete.start, 0)
    
    // Update display
    updateDisplayContent()
    emits('update:modelValue', plainText.value)
    
    // Set cursor position and reset flag
    nextTick(() => {
      setCursorAtPosition(mentionToDelete.start)
      // Reset flag after a short delay
      setTimeout(() => {
        preventMentionSearch.value = false
      }, 100)
    })
    return
  }
  
  // For other cases, let browser handle default backspace behavior
}

// Handle Delete near mention
const handleDeleteNearMention = (event: KeyboardEvent, range: Range, currentNode: Node) => {
  const cursorPos = getCursorPositionInPlainText()
  
  // Check if we're trying to delete a mention
  const mentionToDelete = mentionsData.value.find(mention => 
    cursorPos === mention.start // Cursor right before mention
  )
  
  if (mentionToDelete) {
    event.preventDefault()
    
    // Prevent mention search immediately after deletion
    preventMentionSearch.value = true
    
    // Remove mention from text
    const beforeMention = plainText.value.substring(0, mentionToDelete.start)
    const afterMention = plainText.value.substring(mentionToDelete.end)
    plainText.value = beforeMention + afterMention
    
    // Remove mention from data
    mentionsData.value = mentionsData.value.filter(mention => mention !== mentionToDelete)
    
    // Update mentions positions
    updateMentionsAfterTextInsertion(mentionToDelete.start, mentionToDelete.end - mentionToDelete.start, 0)
    
    // Update display
    updateDisplayContent()
    emits('update:modelValue', plainText.value)
    
    // Set cursor position and reset flag
    nextTick(() => {
      setCursorAtPosition(mentionToDelete.start)
      // Reset flag after a short delay
      setTimeout(() => {
        preventMentionSearch.value = false
      }, 100)
    })
    return
  }
  
  // For other cases, let browser handle default delete behavior
}


// Function to check for mentions
const checkForMention = (text: string) => {
  if (!editorRef.value) return
  
  // Don't show mention search if it's temporarily prevented
  if (preventMentionSearch.value) {
    showUserSearch.value = false
    return
  }
  
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) {
      showUserSearch.value = false
      return
  }
  
  // Get cursor position in plain text
  const cursorPos = getCursorPositionInPlainText()
  
  // Check if cursor is inside or immediately adjacent to an existing mention
  const nearMention = mentionsData.value.some(mention => {
    // Inside mention
    if (cursorPos >= mention.start && cursorPos < mention.end) {
      return true
    }
    // Immediately after mention (within 1 character)
    if (cursorPos >= mention.end && cursorPos <= mention.end + 1) {
      return true
    }
    // Immediately before mention (within 1 character)
    if (cursorPos >= mention.start - 1 && cursorPos < mention.start) {
      return true
    }
    return false
  })
  
  if (nearMention) {
    showUserSearch.value = false
    return
  }
  
  // Get text before cursor
  const textBeforeCursor = text.substring(0, cursorPos)
  const lastAtIndex = textBeforeCursor.lastIndexOf('@')
  
  if (lastAtIndex !== -1) {
    const afterAt = textBeforeCursor.substring(lastAtIndex + 1)
    const prevChar = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ''
    const isWordStart = lastAtIndex === 0 || prevChar === ' ' || prevChar === '\n'
    
    // More strict validation: only letters, digits, underscore, hyphen allowed
    const validAfterAt = /^[a-zA-Z0-9_-]+$/.test(afterAt)
    const isValidMention = afterAt.length > 0 && validAfterAt && isWordStart
    
    if (isValidMention) {
      searchQuery.value = afterAt
      currentMentionStart.value = lastAtIndex
      showUserSearch.value = true
      updateDropdownPosition()
      searchUsers(afterAt)
    } else {
      showUserSearch.value = false
    }
  } else {
    showUserSearch.value = false
  }
}


// Search users with debounce
const searchUsers = debounce(async (query: string) => {
  if (query.length === 0) {
    searchResults.value = []
    return
  }
  
  isSearching.value = true
  selectedUserIndex.value = 0
  
  try {
    const data = await $api.getGrowersLiveSearch({
      keyword: query,
      start: 0,
      limit: 20,
      sortable: 'relavance'
    })
    
    searchResults.value = data.items || []
  } catch (error) {
    console.error('Error searching users:', error)
    searchResults.value = []
  } finally {
    isSearching.value = false
  }
}, 300)

// Ensure highlighted user list item is visible in scroll container
const ensureSelectedItemVisible = () => {
  const container = usersListRef.value
  if (!container) return
  
  const items = container.querySelectorAll('.user-item')
  const index = selectedUserIndex.value
  if (index < 0 || index >= items.length) return
  
  const selectedItem = items[index] as HTMLElement
  const containerRect = container.getBoundingClientRect()
  const itemRect = selectedItem.getBoundingClientRect()
  
  // Check if item is above visible area
  if (itemRect.top < containerRect.top) {
    container.scrollTop -= (containerRect.top - itemRect.top)
  }
  // Check if item is below visible area
  else if (itemRect.bottom > containerRect.bottom) {
    container.scrollTop += (itemRect.bottom - containerRect.bottom)
  }
}

// Select user
const selectUser = (user: User) => {
  const mentionStart = currentMentionStart.value
  const queryLength = searchQuery.value.length
  
  if (mentionStart < 0) return
  
  // Replace @query with mention in plain text
  const beforeMention = plainText.value.substring(0, mentionStart)
  const afterMention = plainText.value.substring(mentionStart + 1 + queryLength) // +1 for @
  const mentionText = `@${user.name}`
  
  // Update plain text - preserve all whitespace and line breaks
  plainText.value = beforeMention + mentionText + ' ' + afterMention
  
  // Update positions of existing mentions after text replacement
  const deletedLength = 1 + queryLength // @ + query
  const insertedLength = mentionText.length + 1 // mention + space
  updateMentionsAfterTextInsertion(mentionStart, deletedLength, insertedLength)
  
  // Add new mention data
  const newMention: MentionData = {
    userId: user.id,
    userName: user.name,
    start: mentionStart,
    end: mentionStart + mentionText.length
  }
  
  // Add the new mention
  mentionsData.value.push(newMention)
  
  showUserSearch.value = false
  
  // Update display with internal flag to prevent input events
  isInternalUpdate.value = true
  updateDisplayContent()
  
  // Emit events
  emits('mention', { user, text: plainText.value })
  emits('update:modelValue', plainText.value)
  
  // Focus and set cursor after mention and space
  nextTick(() => {
    isInternalUpdate.value = false
    if (editorRef.value) {
      editorRef.value.focus()
      setCursorAtPosition(mentionStart + mentionText.length + 1) // +1 for space
    }
  })
}

// Set cursor at specific position in plain text
const setCursorAtPosition = (position: number) => {
  if (!editorRef.value) return
  
  const editor = editorRef.value
  const selection = window.getSelection()
  if (!selection) return
  
  try {
    const range = document.createRange()
    let currentPosition = 0
    let targetNode: Node | null = null
    let targetOffset = 0
    
    // Walk through nodes to find the position
    const walker = document.createTreeWalker(
      editor,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element
            // Skip mention elements entirely in positioning
            if (element.classList.contains('mention')) {
              return NodeFilter.FILTER_REJECT
            }
            if (element.tagName.toLowerCase() === 'br') {
              return NodeFilter.FILTER_ACCEPT
            }
            return NodeFilter.FILTER_SKIP
          }
          return NodeFilter.FILTER_ACCEPT
        }
      }
    )
    
    let node = walker.nextNode()
    while (node && currentPosition < position) {
      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = node.textContent?.replace(/\u00A0/g, ' ').length || 0
        if (currentPosition + textLength >= position) {
          targetNode = node
          targetOffset = position - currentPosition
          break
        }
        currentPosition += textLength
      } else if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === 'br') {
        if (currentPosition >= position) {
          targetNode = node
          targetOffset = 0
          break
        }
        currentPosition += 1 // \n character
      }
      node = walker.nextNode()
    }
    
    // Process mentions separately
    for (const mention of mentionsData.value) {
      if (position >= mention.start && position < mention.end) {
        // Cursor is inside a mention, place it after the mention
        const mentionElements = editor.querySelectorAll('.mention')
        for (const mentionEl of mentionElements) {
          if (mentionEl.getAttribute('data-user-name') === mention.userName) {
            targetNode = mentionEl.nextSibling
            targetOffset = 0
            break
          }
        }
        break
      }
    }
    
    if (targetNode) {
      range.setStart(targetNode, targetOffset)
  range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    } else {
      // Fallback: set cursor at the end
      range.selectNodeContents(editor)
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)
    }
  } catch (error) {
    console.warn('Failed to set cursor position:', error)
  }
}

// Get cursor position (legacy function, use getCursorPositionInPlainText for new code)
const getCursorPosition = (): number => {
  return getCursorPositionInPlainText()
}

// Update cursor position
const updateCursorPosition = () => {
  // Логика для обновления состояния курсора
}

// Auto-resize
const handleAutoResize = () => {
  if (!editorRef.value || !props.autoresize) return
  
  const editor = editorRef.value
  
  // Temporarily reset height for accurate measurement
  editor.style.height = 'auto'
  
  // Get minimum and current height
  const scrollHeight = editor.scrollHeight
  const currentMinHeight = minHeight.value
  
  // Set height not less than minimum
  const newHeight = Math.max(scrollHeight, currentMinHeight)
  editor.style.height = `${newHeight}px`
}

// Update dropdown position
const updateDropdownPosition = () => {
  if (!editorRef.value) return
  
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) {
    // Fallback: position near editor if no selection
    const editorRect = editorRef.value.getBoundingClientRect()
    dropdownStyle.value = {
      top: `${editorRect.height + 5}px`,
      left: '0px'
    }
    return
  }
  
  const range = selection.getRangeAt(0)
  const rect = range.getBoundingClientRect()
  const editorRect = editorRef.value.getBoundingClientRect()
  
  // Check if getBoundingClientRect returns valid coordinates
  if (rect.left === 0 && rect.top === 0 && rect.width === 0 && rect.height === 0) {
    // Invalid rect, fallback to editor position
  dropdownStyle.value = {
      top: `${editorRect.height + 5}px`,
      left: '0px'
    }
    return
  }
  
  // Calculate position relative to editor
  const relativeTop = rect.bottom - editorRect.top + 5
  const relativeLeft = Math.max(0, rect.left - editorRect.left)
  
  // Ensure dropdown doesn't go outside editor bounds
  const maxLeft = Math.max(0, editorRect.width - 200) // 200px is min dropdown width
  
  dropdownStyle.value = {
    top: `${relativeTop}px`,
    left: `${Math.min(relativeLeft, maxLeft)}px`
  }
}

// Watch for content updates
watch(() => props.modelValue, (newValue) => {
  if (plainText.value !== newValue) {
    // Update internal state
    plainText.value = newValue
    
    // Parse mentions from text if needed
    const mentionRegex = /@([a-zA-Z0-9_]+)(?=[^a-zA-Z0-9_]|$)/g
    const matches = [...newValue.matchAll(mentionRegex)]
    
    // Clear existing mentions and add new ones from text
    mentionsData.value = matches.map(match => ({
      userId: 0, // Will be resolved later if needed
      userName: match[1],
      start: match.index!,
      end: match.index! + match[0].length
    }))
    
    // Update display
    nextTick(() => {
      isInternalUpdate.value = true
      updateDisplayContent()
      
      nextTick(() => {
        isInternalUpdate.value = false
        // Auto-resize after content update
      if (props.autoresize) {
        handleAutoResize()
      }
      })
    })
  }
})

// Watch for minimum height updates when rows change
watch(() => props.rows, () => {
  nextTick(() => {
    initMinHeight()
    if (props.autoresize) {
      handleAutoResize()
    }
  })
})

// Initialize minimum height
const initMinHeight = () => {
  if (!editorRef.value) return
  
  const editor = editorRef.value
  const styles = window.getComputedStyle(editor)
  const lineHeight = parseInt(styles.lineHeight) || 20
  const paddingTop = parseInt(styles.paddingTop) || 0
  const paddingBottom = parseInt(styles.paddingBottom) || 0
  const borderTop = parseInt(styles.borderTopWidth) || 0
  const borderBottom = parseInt(styles.borderBottomWidth) || 0
  
  minHeight.value = (lineHeight * props.rows) + paddingTop + paddingBottom + borderTop + borderBottom
  
  if (props.autoresize) {
    editor.style.height = `${minHeight.value}px`
    editor.style.minHeight = `${minHeight.value}px`
  }
}

// Initialization
onMounted(() => {
  // Initialize minimum height
  nextTick(() => {
    initMinHeight()
    
    // If there's initial value, initialize internal state
    if (props.modelValue) {
      plainText.value = props.modelValue
      
      // Parse mentions from initial text
      const mentionRegex = /@([a-zA-Z0-9_]+)(?=[^a-zA-Z0-9_]|$)/g
      const matches = [...props.modelValue.matchAll(mentionRegex)]
      
      mentionsData.value = matches.map(match => ({
        userId: 0, // Will be resolved later if needed
        userName: match[1],
        start: match.index!,
        end: match.index! + match[0].length
      }))
      
      // Update display
      updateDisplayContent()
    } else {
      // Initialize empty state
      plainText.value = ''
      mentionsData.value = []
      displayContent.value = '<br>' // Ensure cursor visibility
    }
  })
})
</script>
   

<style scoped>
.textarea-modern-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
}

.textarea-modern {
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;

  border-radius: 8px;
  padding: 12px;
  /* font-size: 14px; */
  line-height: 1.5;
  outline: none;
  transition: border-color 0.2s ease;
  white-space: pre-wrap; /* Preserve whitespace and line breaks */

}

/* Styles for auto-resize */
.textarea-modern.autoresize {
  max-height: none;
  overflow-y: hidden;
  resize: none;
}

.textarea-modern:empty:before {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
}

.textarea-modern.focused {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.textarea-modern.disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Styles for mentions */
.textarea-modern :deep(.mention) {
  background-color: #dbeafe;
  color: #1d4ed8;
  padding: 0px 1px;
  border-radius: 4px;
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  /* margin: 0 1px; */
  user-select: none; /* Prevent selection */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  white-space: nowrap; /* Prevent wrapping */
}

.textarea-modern :deep(.mention):hover {
  background-color: #bfdbfe;
}

/* Special styles for non-editable mentions */
.textarea-modern :deep(.mention[contenteditable="false"]) {
  outline: none;
  border: 1px solid transparent;
}

.textarea-modern :deep(.mention[contenteditable="false"]:focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

/* Dropdown for user search */
.user-search-dropdown {
  position: absolute;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  min-width: 200px;
}

.loading {
  padding: 12px;
  text-align: center;
  color: #6b7280;
}

.users-list {
  padding: 4px 0;
}

.user-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-item:hover,
.user-item.selected {
  background-color: #f3f4f6;
}

.user-avatar {
  width: 24px;
  border-radius: 50%;
  object-fit: cover;
  aspect-ratio: 1/1;
  object-fit: cover;
}

.user-name {
  font-weight: 500;
  color: #111827;
}

.no-results {
  padding: 12px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}
</style>
